import React, { useState, useMemo } from "react";
import {
  calculateQualityScore,
  getQualityLabel,
  getTimePattern,
  sumDailyStats,
  getDateRange,
} from "./services/AnalyticsEngine";

// ─── CONFIG ──────────────────────────────────────────────────
const WORKER_URL = "https://supermedia-newsletter.procreaction.workers.dev";

// ─── HILFSFUNKTIONEN ─────────────────────────────────────────
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const formatDate = (seconds) => {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

// ─── MINI BAR ────────────────────────────────────────────────
const MiniBar = ({ value, max, color = "#00acc1" }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ flex: 1, height: "5px", background: "rgba(255,255,255,0.08)", borderRadius: "2px", overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: "2px", transition: "width 0.5s ease" }} />
    </div>
  );
};

// ─── TREND INDICATOR ─────────────────────────────────────────
const Trend = ({ current, previous }) => {
  if (previous === undefined || previous === null || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return null;
  return (
    <span style={{ fontSize: "0.6rem", color: pct > 0 ? "#00acc1" : "#ff1744", marginLeft: "4px" }}>
      {pct > 0 ? "↑" : "↓"}{Math.abs(pct)}%
    </span>
  );
};

// ─── COLLAPSIBLE SECTION ─────────────────────────────────────
const CollapsibleSection = ({ icon, title, count, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%", padding: "10px 0",
          background: "none", border: "none",
          borderBottom: isOpen ? "1px solid rgba(0,172,193,0.3)" : "1px solid #222",
          cursor: "pointer", marginBottom: isOpen ? "12px" : "0",
          display: "flex", alignItems: "center", gap: "8px",
        }}
      >
        <span style={{ fontSize: "0.9rem" }}>{icon}</span>
        <span style={{ fontSize: "0.72rem", fontWeight: "900", letterSpacing: "2px", color: isOpen ? "#00acc1" : "#777" }}>{title}</span>
        {count !== undefined && (
          <span style={{ fontSize: "0.6rem", color: "#555", background: "rgba(255,255,255,0.03)", padding: "2px 8px", borderRadius: "10px", border: "1px solid #1a1a1a" }}>
            {count}
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: "0.6rem", color: isOpen ? "#00acc1" : "#555" }}>
          {isOpen ? "▲" : "▼"}
        </span>
      </button>
      {isOpen && <div style={{ marginBottom: "8px" }}>{children}</div>}
    </div>
  );
};

// ─── HAUPT KOMPONENTE ─────────────────────────────────────────
export default function ResumeDashboard({ tracks = [], allUsers = [], showToast }) {
  const [isOpen, setIsOpen] = useState(false);
  const [zeitraum, setZeitraum] = useState("7");
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null); // userId
  const [sendingWeeklyReport, setSendingWeeklyReport] = useState(false);
  const [weeklyReportSent, setWeeklyReportSent] = useState(false);
  const [newFeaturesText, setNewFeaturesText] = useState("");
  const [showWeeklyReportPanel, setShowWeeklyReportPanel] = useState(false);

  const zeitraumDays = parseInt(zeitraum);

  const stats = useMemo(() => {
    const safeTracks = Array.isArray(tracks) ? tracks : [];
    const safeUsers = Array.isArray(allUsers) ? allUsers : [];

    const now = Date.now();
    const since = now - zeitraumDays * 24 * 60 * 60 * 1000;
    const sincePrev = since - zeitraumDays * 24 * 60 * 60 * 1000;

    const currentDates = getDateRange(zeitraumDays);
    const prevDates = getDateRange(zeitraumDays * 2).slice(0, zeitraumDays);

    // ── Datenverfügbarkeit ──
    const allDailyDates = new Set();
    safeTracks.forEach(t => {
      if (t.dailyStats) Object.keys(t.dailyStats).forEach(d => allDailyDates.add(d));
    });
    const daysWithData = allDailyDates.size;
    const hasDailyData = daysWithData > 0;
    const dataNote = hasDailyData
      ? daysWithData < zeitraumDays ? `⚠️ NUR ${daysWithData} VON ${zeitraumDays} TAGEN MIT DATEN` : null
      : `⚠️ NOCH KEINE ZEITREIHEN-DATEN – TRACKING LÄUFT AB HEUTE`;

    // ── Neue Tracks & Members ──
    const newTracks = safeTracks.filter(t => (t.createdAt?.seconds * 1000) > since);
    const prevNewTracks = safeTracks.filter(t => {
      const ts = t.createdAt?.seconds * 1000;
      return ts > sincePrev && ts <= since;
    });
    const newUsers = safeUsers.filter(u => (u.createdAt?.seconds * 1000) > since);
    const newArtists = safeUsers.filter(u => u.isArtist && (u.createdAt?.seconds * 1000) > since);
    const activeListeners = safeUsers.filter(u => (u.lastLogin?.seconds * 1000) > since).length;
    const totalListeners = safeUsers.filter(u => u.role === "selector" || u.role === "listener").length;

    // ── Plays/Skips/Completions/Likes ──
    const periodPlays = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'plays'), 0);
    const prevPlays = safeTracks.reduce((s, t) => s + sumDailyStats(t, prevDates, 'plays'), 0);
    const periodSkips = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'skips'), 0);
    const prevSkips = safeTracks.reduce((s, t) => s + sumDailyStats(t, prevDates, 'skips'), 0);
    const periodCompletions = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'completions'), 0);
    const completionRate = periodPlays > 0 ? Math.round((periodCompletions / periodPlays) * 100) : 0;
    const periodLikes = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'likes'), 0);
    const prevLikes = safeTracks.reduce((s, t) => s + sumDailyStats(t, prevDates, 'likes'), 0);
    const totalLikes = safeTracks.reduce((s, t) => s + (t.likes?.length || 0), 0);
    const totalDislikes = safeTracks.reduce((s, t) => s + (t.dislikes?.length || 0), 0);

    // ── Quality Score pro Track ──
    const tracksWithQScore = safeTracks.map(t => {
      const periodPlaysT = sumDailyStats(t, currentDates, 'plays');
      const periodCompT = sumDailyStats(t, currentDates, 'completions');
      const periodSkipsT = sumDailyStats(t, currentDates, 'skips');
      const qScore = calculateQualityScore(t, currentDates);
      const prevQScore = calculateQualityScore(t, prevDates);
      const qScoreDelta = qScore - prevQScore;
      const timePattern = getTimePattern(t);
      const completionRateT = periodPlaysT > 0 ? Math.round((periodCompT / periodPlaysT) * 100) : 0;
      return {
        ...t, qScore, prevQScore, qScoreDelta,
        periodPlays: periodPlaysT, periodSkips: periodSkipsT,
        periodCompletions: periodCompT, completionRate: completionRateT,
        timePattern, qualityLabel: getQualityLabel(qScore),
      };
    });

    // ── Top 10 Tracks ──
    const top10Fallback = [...tracksWithQScore]
      .filter(t => t.qScore > 0 || t.plays > 0)
      .sort((a, b) => b.qScore - a.qScore)
      .slice(0, 10);

    const top10Display = top10Fallback.length > 0
      ? top10Fallback
      : [...safeTracks].sort((a, b) => (b.plays || 0) - (a.plays || 0)).slice(0, 10)
          .map(t => ({ ...t, qScore: calculateQualityScore(t), periodPlays: t.plays || 0, completionRate: 0, qScoreDelta: 0 }));

    // ── TOP PLUS MOVERS ──
    const topPlusMovers = [...tracksWithQScore]
      .filter(t => t.qScoreDelta > 0)
      .sort((a, b) => b.qScoreDelta - a.qScoreDelta)
      .slice(0, 5);

    // ── TOP MINUS MOVERS ──
    const topMinusMovers = [...tracksWithQScore]
      .filter(t => t.qScoreDelta < 0)
      .sort((a, b) => a.qScoreDelta - b.qScoreDelta)
      .slice(0, 5);

    // ── Stagnante Tracks ──
    const stagnantTracks = tracksWithQScore
      .filter(t => t.plays > 10 && t.periodPlays === 0).slice(0, 5);

    // ── ON/OFF AIR Bewegungen ──
    const onAirMovements = [];
    safeTracks.forEach(t => {
      if (!Array.isArray(t.onAirLog)) return;
      t.onAirLog.forEach(entry => {
        const entryMs = entry.changedAt?.seconds
          ? entry.changedAt.seconds * 1000
          : entry.changedAt instanceof Date ? entry.changedAt.getTime()
          : new Date(entry.changedAt).getTime();
        if (entryMs > since) {
          onAirMovements.push({ trackTitle: t.title, trackArtist: t.artist, changedTo: entry.changedTo, changedAt: entryMs, qScore: t.qScore });
        }
      });
    });
    onAirMovements.sort((a, b) => b.changedAt - a.changedAt);
    const newOnAir = onAirMovements.filter(m => m.changedTo === true);
    const newOffAir = onAirMovements.filter(m => m.changedTo === false);

    // ── Peak Hours – Top 10 ──
    const hourTotals = {};
    safeTracks.forEach(t => {
      if (!t.playsByHour) return;
      Object.entries(t.playsByHour).forEach(([hour, count]) => {
        hourTotals[hour] = (hourTotals[hour] || 0) + count;
      });
    });
    const top10Hours = Object.entries(hourTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([hour, plays]) => ({ hour: parseInt(hour), plays }));
    const maxHourPlays = top10Hours[0]?.plays || 1;

    // ── Inaktive Artists ──
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
    const inactiveArtists = safeUsers
      .filter(u => u.isArtist && !u.isAdmin)
      .filter(u => {
        const userTracks = safeTracks.filter(t => t.userId === u.id);
        if (userTracks.length === 0) return true;
        const lastUpload = Math.max(...userTracks.map(t => t.createdAt?.seconds * 1000 || 0));
        return lastUpload < thirtyDaysAgo;
      });

    return {
      newTracksCount: newTracks.length, prevTracksCount: prevNewTracks.length,
      newUsersCount: newUsers.length, newArtistsCount: newArtists.length,
      activeListeners, totalListeners,
      periodPlays, prevPlays, periodSkips, prevSkips,
      periodCompletions, completionRate,
      periodLikes, prevLikes, totalLikes, totalDislikes,
      top10Display, topPlusMovers, topMinusMovers, stagnantTracks,
      onAirMovements, newOnAir, newOffAir,
      newUsers, inactiveArtists,
      top10Hours, maxHourPlays,
      kw: getWeekNumber(new Date()),
      hasDailyData, daysWithData, dataNote,
    };
  }, [tracks, allUsers, zeitraum]);

  // ─── AI ZUSAMMENFASSUNG ────────────────────────────────────
  const generateAiSummary = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiSummary("");
    try {
      const prompt = `Du bist Radio-Manager bei streetwise RADIO, einem Community Radio Collective.
Analysiere diese Wochendaten und schreibe eine kurze, präzise Zusammenfassung auf Deutsch.
Sei direkt, konkret und hebe die wichtigsten Erkenntnisse hervor.


ZEITRAUM: Letzte ${zeitraum} Tage (KW ${stats.kw})

AKTIVITÄT:
- Neue Tracks: ${stats.newTracksCount}
- Neue Members: ${stats.newUsersCount}
- Plays: ${stats.periodPlays} (vorher: ${stats.prevPlays})
- Completion Rate: ${stats.completionRate}%
- Skips: ${stats.periodSkips}
- Likes: ${stats.periodLikes}

TOP 3 TRACKS:
${stats.top10Display.slice(0,3).map((t,i) => `${i+1}. "${t.title}" von ${t.artist} | Q${t.qScore} | ${t.completionRate}% Completion`).join('\n')}

GRÖSSTE GEWINNER:
${stats.topPlusMovers.slice(0,3).map(t => `- "${t.title}": +${t.qScoreDelta} QScore`).join('\n') || "Keine"}

GRÖSSTE VERLIERER:
${stats.topMinusMovers.slice(0,3).map(t => `- "${t.title}": ${t.qScoreDelta} QScore`).join('\n') || "Keine"}

ON AIR: ${stats.newOnAir.map(m => m.trackTitle).join(', ') || "—"}
OFF AIR: ${stats.newOffAir.map(m => m.trackTitle).join(', ') || "—"}

INAKTIVE ARTISTS: ${stats.inactiveArtists.length}


Format:
📻 WOCHE IN KÜRZE
[2-3 Sätze Gesamtbild]

🔥 HIGHLIGHTS
[2-3 konkrete Punkte]

⚠️ ZU BEACHTEN
[1-2 Punkte die Aufmerksamkeit brauchen]

💡 EMPFEHLUNG
[1 konkrete Handlungsempfehlung]`;

      const res = await fetch(`${WORKER_URL}/claude`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-5",
          max_tokens: 800,
          messages: [{ role: "user", content: prompt }]
        })
      });
      const data = await res.json();
      setAiSummary(data.content?.[0]?.text || "Keine Antwort erhalten.");
    } catch(e) {
      setAiSummary("Fehler beim Laden. Bitte nochmal versuchen.");
      console.error("AI Summary Error:", e);
    } finally {
      setAiLoading(false);
    }
  };

  // ─── EMAIL SENDEN ──────────────────────────────────────────
  const generateEmailHtml = () => {
    const summary = aiSummary ? `<div style="background:#1a1a1a;padding:15px;border-radius:8px;margin-bottom:20px;border-left:3px solid #00acc1"><pre style="color:#ccc;font-family:sans-serif;font-size:14px;white-space:pre-wrap;margin:0">${aiSummary}</pre></div>` : "";

    return `
<!DOCTYPE html>
<html>
<body style="background:#000;color:#fff;font-family:sans-serif;padding:20px;max-width:600px;margin:0 auto">
  <h1 style="color:#00acc1;font-size:1.2rem;letter-spacing:3px">📻 STREETWISE RADIO</h1>
  <h2 style="color:#fff;font-size:1rem">RESUMÉ KW ${stats.kw} – LETZTE ${zeitraum} TAGE</h2>
  ${summary}
  <h3 style="color:#00acc1;font-size:0.8rem;letter-spacing:2px">AKTIVITÄT</h3>
  <table style="width:100%;border-collapse:collapse">
    ${[
      ["Neue Tracks", stats.newTracksCount],
      ["Neue Members", stats.newUsersCount],
      ["Plays", stats.periodPlays],
      ["Completion Rate", `${stats.completionRate}%`],
      ["Skips", stats.periodSkips],
      ["Likes", stats.periodLikes],
    ].map(([label, val]) => `<tr><td style="padding:6px 0;color:#888;font-size:0.8rem">${label}</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${val}</td></tr>`).join('')}
  </table>
  <h3 style="color:#00acc1;font-size:0.8rem;letter-spacing:2px;margin-top:20px">TOP 10 TRACKS</h3>
  ${stats.top10Display.map((t, i) => `<div style="padding:6px 0;border-bottom:1px solid #111"><span style="color:#555">${i+1}.</span> <strong style="color:#fff">${t.title}</strong> <span style="color:#666">– ${t.artist}</span> <span style="color:#00acc1;float:right">Q${t.qScore}</span></div>`).join('')}
  <h3 style="color:#00acc1;font-size:0.8rem;letter-spacing:2px;margin-top:20px">TOP MOVER ↑</h3>
  ${stats.topPlusMovers.map(t => `<div style="padding:4px 0"><span style="color:#00acc1">+${t.qScoreDelta}</span> ${t.title}</div>`).join('') || '<div style="color:#555">Keine</div>'}
  <h3 style="color:#ff1744;font-size:0.8rem;letter-spacing:2px;margin-top:20px">TOP MOVER ↓</h3>
  ${stats.topMinusMovers.map(t => `<div style="padding:4px 0"><span style="color:#ff1744">${t.qScoreDelta}</span> ${t.title}</div>`).join('') || '<div style="color:#555">Keine</div>'}
  <p style="color:#333;font-size:0.7rem;margin-top:30px">streetwise RADIO – Collective Studio</p>
</body>
</html>`;
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      // Alle Admin-Emails sammeln
      const adminEmails = allUsers
        .filter(u => u.isAdmin && u.email)
        .map(u => ({ email: u.email, name: u.name || "Admin" }));

      if (adminEmails.length === 0) {
        showToast?.("Keine Admin-Email gefunden.", "error");
        return;
      }

      const res = await fetch(`${WORKER_URL}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `📻 streetwise RADIO – Resumé KW ${stats.kw}`,
          html: generateEmailHtml(),
          contacts: adminEmails,
          testMode: false,
        })
      });
      const data = await res.json();
      showToast?.(`Resumé gesendet an ${data.sent} Admin(s)! ✅`);
    } catch(e) {
      showToast?.("Email-Versand fehlgeschlagen.", "error");
      console.error(e);
    } finally {
      setSendingEmail(false);
    }
  };

  // ─── REMINDER EMAIL ────────────────────────────────────────
  const handleSendReminder = async (artist) => {
    if (!artist.email) {
      showToast?.("Keine Email-Adresse für diesen Artist.", "error");
      return;
    }
    setSendingReminder(artist.id);
    try {
      const html = `
<!DOCTYPE html>
<html>
<body style="background:#000;color:#fff;font-family:sans-serif;padding:20px;max-width:600px;margin:0 auto">
  <h1 style="color:#00acc1;font-size:1.2rem;letter-spacing:3px">📻 STREETWISE RADIO</h1>
  <h2 style="color:#fff">Hey ${artist.name || "Artist"}!</h2>
  <p style="color:#ccc;line-height:1.6">
    Wir vermissen deine Musik im Collective! Es ist eine Weile her seit deinem letzten Upload.
  </p>
  <p style="color:#ccc;line-height:1.6">
    Das Collective lebt von der Energie und Kreativität jedes Artists. Hast du gerade etwas in der Mache? 
    Wir würden uns freuen, neue Tracks von dir zu hören.
  </p>
  <div style="background:#1a1a1a;padding:15px;border-radius:8px;border-left:3px solid #00acc1;margin:20px 0">
    <p style="color:#00acc1;margin:0;font-weight:bold">💡 Jetzt einloggen und neuen Track hochladen:</p>
    <a href="https://radio.streetwise.studio" style="color:#fff">radio.streetwise.studio</a>
  </div>
  <div style="background:#111;padding:15px;border-radius:10px;text-align:center;margin:20px 0">
  <a href="https://radio.streetwise.studio" style="display:inline-block;background:#00acc1;color:#000;padding:12px 30px;border-radius:30px;text-decoration:none;font-weight:900;font-size:0.85rem;letter-spacing:1px">
    📻 JETZT REINHÖREN
  </a>
</div>
  <p style="color:#666;font-size:0.8rem">Bei Fragen schreib uns direkt in der App.</p>
  <p style="color:#333;font-size:0.7rem;margin-top:30px">streetwise RADIO – Collective Studio</p>
</body>
</html>`;

      const res = await fetch(`${WORKER_URL}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: "📻 streetwise RADIO – Wir vermissen dich!",
          html,
          contacts: [{ email: artist.email, name: artist.name || "Artist" }],
          testMode: false,
        })
      });
      const data = await res.json();
      if (data.sent > 0) {
        showToast?.(`Erinnerung an ${artist.name} gesendet! ✉️`);
      } else {
        showToast?.("Email konnte nicht gesendet werden.", "error");
      }
    } catch(e) {
      showToast?.("Fehler beim Senden.", "error");
      console.error(e);
    } finally {
      setSendingReminder(null);
    }
  };

  // ─── WEEKLY ARTIST REPORT ──────────────────────────────────
  const handleSendWeeklyReport = async (specificArtist = null) => {
    const targetArtists = specificArtist 
      ? [specificArtist] 
      : allUsers.filter(u => (u.isArtist || u.isAdmin) && u.email);
      console.log("Target Artists:", targetArtists.map(a => ({ name: a.name, email: a.email, id: a.id })));

    if (targetArtists.length === 0) {
      showToast?.("Keine Artists mit Email gefunden.", "error");
      return;
    }

    setSendingWeeklyReport(true);
    setWeeklyReportSent(false);
    let sentCount = 0;

    const safeTracks = Array.isArray(tracks) ? tracks : [];
    const currentDates = getDateRange(7);
    const kw = getWeekNumber(new Date());

    // Globale Stats (bleiben gleich für alle)
    const globalPlays = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'plays'), 0);
    const globalCompletions = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'completions'), 0);
    const globalLikes = safeTracks.reduce((s, t) => s + sumDailyStats(t, currentDates, 'likes'), 0);
    const globalListeners = allUsers.filter(u => (u.lastLogin?.seconds * 1000) > Date.now() - 7 * 24 * 60 * 60 * 1000).length;
    const globalCompletionRate = globalPlays > 0 ? Math.round((globalCompletions / globalPlays) * 100) : 0;

    const hourTotals = {};
    safeTracks.forEach(t => {
      if (!t.playsByHour) return;
      Object.entries(t.playsByHour).forEach(([h, c]) => { hourTotals[h] = (hourTotals[h] || 0) + c; });
    });
    const peakHour = Object.entries(hourTotals).sort((a, b) => b[1] - a[1])[0];

    for (const artist of targetArtists) {
      try {
        // Filterung der Tracks des Artists für den Zeitraum
        const myTracks = safeTracks
          .filter(t => t.userId === artist.id)
          .map(t => ({
            ...t,
            periodPlays: sumDailyStats(t, currentDates, 'plays'),
            periodCompletions: sumDailyStats(t, currentDates, 'completions'),
            qScore: calculateQualityScore(t, currentDates),
            timePattern: getTimePattern(t),
          }))
          .sort((a, b) => b.qScore - a.qScore)
          .slice(0, 5);

        // KORREKTUR: Nur Plays der Woche zählen
        const totalArtistPlaysWeek = myTracks.reduce((s, t) => s + (t.periodPlays || 0), 0);
        const totalArtistLikesWeek = myTracks.reduce((s, t) => s + (t.likes?.length || 0), 0);

        let hotTake = "";
        if (myTracks.length > 0) {
          try {
            const prompt = `Du bist der freundliche Host und Produzent von streetwise RADIO. 
Schreibe einen kurzen, positiven Hot Take (2-3 Sätze auf Deutsch) für "${artist.name}".
Beobachte den Vibe, nicht die Verkaufszahlen. Erwähne "${myTracks[0].title}". 
Formuliere es als Inspiration: "Es wäre spannend, mehr in diesem Stil zu hören" statt "Du musst...". 
Kein "WAS ZU TUN IST". Keine Tabellen-Begriffe wie "Completion Rate".`;

            const aiRes = await fetch(`${WORKER_URL}/claude`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                model: "claude-sonnet-5",
                max_tokens: 300,
                messages: [{ role: "user", content: prompt }]
              })
            });
            const aiData = await aiRes.json();
            hotTake = aiData.content?.[0]?.text || "";
          } catch(e) { console.warn("AI failed for", artist.name); }
        }

        const html = `<!DOCTYPE html>
<html>
<body style="background:#0a0a0a;color:#fff;font-family:'Helvetica Neue',sans-serif;padding:0;margin:0">
  <div style="max-width:600px;margin:0 auto;padding:30px 20px">
    <div style="border-bottom:1px solid #1a1a1a;padding-bottom:20px;margin-bottom:25px">
      <p style="color:#00acc1;font-size:0.7rem;letter-spacing:4px;margin:0 0 8px">📻 STREETWISE RADIO</p>
      <h1 style="color:#fff;font-size:1.4rem;font-weight:900;margin:0;letter-spacing:2px">Weekly Resumé</h1>
      <p style="color:#555;font-size:0.8rem;margin:6px 0 0">KW ${kw} – Letzte 7 Tage</p>
    </div>

    <p style="color:#ccc;font-size:1rem;line-height:1.6;margin-bottom:25px">
      Hey ${artist.name || "Artist"}! Hier kommt dein wöchentliches Resumé zu deinen Tracks im streetwise Radio.
    </p>

    ${myTracks.length > 0 ? `
    <div style="margin-bottom:25px">
      <h2 style="color:#00acc1;font-size:0.7rem;letter-spacing:3px;margin:0 0 12px">DEINE TOP ${Math.min(5, myTracks.length)} TRACKS DIESE WOCHE</h2>
      ${myTracks.map((t, i) => `
      <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #1a1a1a">
        <div>
          <span style="color:#555;font-size:0.75rem">${i+1}. </span>
          <strong style="color:#fff;font-size:0.9rem">${t.title}</strong>
          ${t.timePattern ? `<span style="color:${t.timePattern.color};font-size:0.7rem;margin-left:8px">${t.timePattern.label}</span>` : ""}
          <br>
          <span style="color:#555;font-size:0.75rem">${t.periodPlays} Plays${t.periodCompletions > 0 ? ` · ${t.periodCompletions} Completions` : ""}</span>
        </div>
        <span style="color:#00acc1;font-weight:900;font-size:0.75rem">${getQualityLabel(t.qScore).label}</span>
      </div>`).join('')}
    </div>
    ` : `
    <div style="background:#111;padding:15px;border-radius:8px;margin-bottom:25px;border-left:3px solid #333">
      <p style="color:#555;margin:0;font-size:0.85rem">Diese Woche noch keine Track-Aktivität. Lade bald neue Tracks hoch! 🎵</p>
    </div>
    `}

    ${hotTake ? `
    <div style="background:#111;padding:20px;border-radius:10px;margin-bottom:25px;border-left:3px solid #00acc1">
      <p style="color:#00acc1;font-size:0.65rem;letter-spacing:3px;margin:0 0 10px;font-weight:900">🔥 HOT TAKE</p>
      <p style="color:#ddd;font-size:0.9rem;line-height:1.7;margin:0;white-space:pre-wrap">${hotTake}</p>
    </div>
    ` : ""}

    ${newFeaturesText ? `
    <div style="margin-bottom:25px">
      <h2 style="color:#00acc1;font-size:0.7rem;letter-spacing:3px;margin:0 0 12px">✨ NEUE FEATURES IN DER APP</h2>
      <p style="color:#ccc;font-size:0.85rem;line-height:1.6;margin:0">${newFeaturesText.replace(/\n/g, '<br>')}</p>
    </div>
    ` : ""}

    <div style="margin-bottom:25px">
      <h2 style="color:#00acc1;font-size:0.7rem;letter-spacing:3px;margin:0 0 12px">📊 RADIO STATS DIESE WOCHE</h2>
      <table style="width:100%;border-collapse:collapse">
        <tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Total Plays</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${globalPlays}</td></tr>
        <tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Completion Rate</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${globalCompletionRate}%</td></tr>
        <tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Aktive Listeners</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${globalListeners}</td></tr>
        ${peakHour ? `<tr><td style="padding:6px 0;color:#666;font-size:0.8rem">Peak Hour</td><td style="padding:6px 0;color:#fff;font-weight:bold;text-align:right">${peakHour[0]}:00 Uhr</td></tr>` : ""}
      </table>
    </div>

    <div style="background:#111;padding:20px;border-radius:10px;margin-bottom:25px;border-left:3px solid #555">
      <p style="color:#00acc1;font-size:0.65rem;letter-spacing:3px;margin:0 0 8px;font-weight:900">💡 DEINE IDEEN ZÄHLEN</p>
      <p style="color:#ccc;font-size:0.85rem;line-height:1.6;margin:0">
        Hast du Ideen oder Feedback? Antworte einfach auf diese Email.<br>
        Ich freue mich bald wieder einen neuen Track von dir zu hören.
      </p>
    </div>

    <div style="background:#111;padding:15px;border-radius:10px;text-align:center;margin-bottom:25px">
      <a href="https://radio.streetwise.studio" style="display:inline-block;background:#00acc1;color:#000;padding:12px 30px;border-radius:30px;text-decoration:none;font-weight:900;font-size:0.85rem;letter-spacing:1px">
        📻 JETZT REINHÖREN
      </a>
    </div>

    <div style="border-top:1px solid #1a1a1a;padding-top:20px">
      <p style="color:#ccc;font-size:0.85rem;margin:0">Greetings</p>
      <p style="color:#fff;font-weight:900;margin:4px 0">Mat</p>
      <p style="color:#555;font-size:0.8rem;margin:0">streetwise.STUDIO</p>
    </div>
  </div>
</body>
</html>`;

        const res = await fetch(`${WORKER_URL}/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: `📻 streetwise RADIO – Weekly Resumé KW ${kw}`,
            html,
            contacts: [{ email: artist.email, name: artist.name || "Artist" }]
          })
        });
        const data = await res.json();
        console.log(`Weekly Report für ${artist.name}:`, res.status, data); // ✅ zeigt uns die echte Antwort

        if (data.sent > 0) {
          sentCount++;
        } else {
          console.error(`❌ Nicht zugestellt an ${artist.name}:`, data);
        }

        
        // Professionelles Rate-Limiting
        if (targetArtists.length > 1) await new Promise(r => setTimeout(r, 300));

      } catch(e) { console.error("Report failed for", artist.name, e); }
    }

    setSendingWeeklyReport(false);
    setWeeklyReportSent(true);
    showToast?.(`${sentCount} Report(s) erfolgreich versendet! ✅`);
  };

  const zeitraumLabel = zeitraum === "7" ? "WOCHE" : `${zeitraum} TAGE`;


  // ─── RENDER ────────────────────────────────────────────────
  return (
    <div style={{ margin: "20px", marginTop: "0" }}>

      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%", padding: "15px 20px",
          background: isOpen ? "rgba(0,172,193,0.06)" : "rgba(255,255,255,0.02)",
          border: isOpen ? "1px solid rgba(0,172,193,0.2)" : "1px solid #1a1a1a",
          borderRadius: isOpen ? "16px 16px 0 0" : "16px",
          color: isOpen ? "#00acc1" : "#666",
          fontWeight: "900", fontSize: "0.78rem",
          letterSpacing: "2px", cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <span>📊 RESUME DASHBOARD</span>
        <span style={{ fontSize: "0.66rem", opacity: 0.7 }}>{isOpen ? "▲ SCHLIESSEN" : `▼ KW ${stats.kw}`}</span>
      </button>

      {isOpen && (
        <div style={{
          background: "#0a0a0a", border: "1px solid #1a1a1a", borderTop: "none",
          borderRadius: "0 0 16px 16px", padding: "20px",
          display: "flex", flexDirection: "column", gap: "20px"
        }}>

          {/* DATA NOTE */}
          {stats.dataNote && (
            <div style={{ padding: "10px 12px", background: "rgba(255,152,0,0.06)", border: "1px solid rgba(255,152,0,0.2)", borderRadius: "8px" }}>
              <span style={{ fontSize: "0.66rem", color: "#ff9800", letterSpacing: "1px" }}>{stats.dataNote}</span>
            </div>
          )}

          {/* ZEITRAUM SELECTOR */}
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <span style={{ fontSize: "0.66rem", color: "#666", letterSpacing: "1px", marginRight: "4px" }}>ZEITRAUM</span>
            {[{ val: "7", label: "7 TAGE" }, { val: "14", label: "14 TAGE" }, { val: "30", label: "30 TAGE" }, { val: "90", label: "90 TAGE" }].map(({ val, label }) => (
              <button key={val} onClick={() => setZeitraum(val)} style={{
                padding: "5px 12px", borderRadius: "6px", border: "none",
                background: zeitraum === val ? "#00acc1" : "rgba(255,255,255,0.06)",
                color: zeitraum === val ? "#000" : "#777",
                fontSize: "0.66rem", fontWeight: "900", cursor: "pointer", letterSpacing: "1px"
              }}>{label}</button>
            ))}
          </div>

          {/* AI ZUSAMMENFASSUNG */}
          <div style={{ padding: "15px", background: "rgba(255,152,0,0.04)", borderRadius: "12px", border: "1px solid rgba(255,152,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: aiSummary ? "12px" : "0" }}>
              <span style={{ fontSize: "0.72rem", color: "#ff9800", fontWeight: "900", letterSpacing: "2px" }}>🤖 KI-ZUSAMMENFASSUNG</span>
              <button
                onClick={generateAiSummary}
                disabled={aiLoading}
                style={{ background: "none", border: "1px solid rgba(255,152,0,0.3)", color: "#ff9800", padding: "4px 12px", borderRadius: "6px", fontSize: "0.6rem", fontWeight: "900", cursor: "pointer" }}
              >
                {aiLoading ? "ANALYSIERE..." : aiSummary ? "NEU GENERIEREN" : "GENERIEREN"}
              </button>
            </div>
            {aiSummary && (
              <div style={{ fontSize: "0.78rem", color: "#ccc", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
                {aiSummary}
              </div>
            )}
            {!aiSummary && !aiLoading && (
              <div style={{ fontSize: "0.66rem", color: "#444", marginTop: "4px" }}>
                KI analysiert die Daten und erstellt eine Zusammenfassung für das Email.
              </div>
            )}
          </div>

          {/* AKTIVITÄT */}
          <CollapsibleSection icon="📈" title={`AKTIVITÄT – LETZTE ${zeitraumLabel}`} defaultOpen={true}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
              {[
                { label: "NEUE TRACKS", val: stats.newTracksCount, icon: "🎵", prev: stats.prevTracksCount },
                { label: "NEUE MEMBERS", val: stats.newUsersCount, icon: "👥" },
                { label: "NEUE ARTISTS", val: stats.newArtistsCount, icon: "🎙️" },
                { label: "AKTIVE LISTENER", val: stats.activeListeners, icon: "🎧", sub: `${stats.totalListeners} gesamt` },
                { label: "PLAYS", val: stats.periodPlays, icon: "▶", prev: stats.prevPlays },
                { label: "COMPLETION RATE", val: `${stats.completionRate}%`, icon: "✅", sub: `${stats.periodCompletions} vollständig` },
                { label: "SKIPS", val: stats.periodSkips, icon: "⏭", prev: stats.prevSkips, color: "#ff1744" },
                { label: "LIKES", val: stats.periodLikes, icon: "♥", prev: stats.prevLikes, sub: `${stats.totalLikes} gesamt · ${stats.totalDislikes} Dislikes` },
              ].map(item => (
                <div key={item.label} style={{ padding: "12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid #1a1a1a" }}>
                  <div style={{ fontSize: "1.1rem", marginBottom: "4px" }}>{item.icon}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                    <div style={{ fontSize: "1.2rem", fontWeight: "900", color: item.color || "#eee" }}>{item.val}</div>
                    {item.prev !== undefined && <Trend current={typeof item.val === 'number' ? item.val : 0} previous={item.prev} />}
                  </div>
                  <div style={{ fontSize: "0.6rem", color: "#666", letterSpacing: "1px", marginTop: "2px" }}>{item.label}</div>
                  {item.sub && <div style={{ fontSize: "0.55rem", color: "#444", marginTop: "1px" }}>{item.sub}</div>}
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* PEAK HOURS */}
          <CollapsibleSection icon="🕐" title="PEAK HOURS – TOP 10" defaultOpen={false}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {stats.top10Hours.length === 0 ? (
                <div style={{ fontSize: "0.72rem", color: "#444", textAlign: "center", padding: "10px" }}>Noch keine Daten</div>
              ) : stats.top10Hours.map(({ hour, plays }, i) => (
                <div key={hour} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "0.72rem", color: i === 0 ? "#00acc1" : "#777", fontWeight: "900", minWidth: "65px" }}>
                    {String(hour).padStart(2, '0')}:00 Uhr
                  </span>
                  <MiniBar value={plays} max={stats.maxHourPlays} color={i < 3 ? "#00acc1" : "#333"} />
                  <span style={{ fontSize: "0.72rem", color: i < 3 ? "#00acc1" : "#777", fontWeight: "900", minWidth: "50px", textAlign: "right" }}>
                    {plays} ▶
                  </span>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* TOP 10 TRACKS */}
          <CollapsibleSection icon="🔥" title="TOP 10 TRACKS – QUALITY SCORE" count={stats.top10Display.length} defaultOpen={false}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {stats.top10Display.map((t, i) => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "0.66rem", color: i < 3 ? "#00acc1" : "#555", fontWeight: "900", minWidth: "18px" }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{ fontSize: "0.84rem", color: "#ddd", fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "140px" }}>
                        {t.title}
                      </div>
                      {t.timePattern && (
                        <span style={{ fontSize: "0.6rem", color: t.timePattern.color, whiteSpace: "nowrap" }}>
                          {t.timePattern.label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "0.66rem", color: "#666" }}>
                      {t.artist}
                      {t.completionRate > 0 && (
                        <span style={{ marginLeft: "6px", color: t.completionRate > 50 ? "#00acc1" : "#666" }}>
                          · {t.completionRate}% ✅
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
                    <span style={{ fontSize: "0.6rem", color: t.qualityLabel?.color || "#555", fontWeight: "900" }}>
                      {t.qualityLabel?.label}
                    </span>
                    <span style={{ fontSize: "0.72rem", color: "#00acc1", fontWeight: "900" }}>Q{t.qScore}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "10px", padding: "6px 10px", background: "rgba(255,255,255,0.02)", borderRadius: "6px" }}>
              <span style={{ fontSize: "0.6rem", color: "#444" }}>Q = Plays×1 + Completion%×10 + Likes×20 − Skips×5</span>
            </div>
          </CollapsibleSection>

          {/* TOP MOVERS */}
          <CollapsibleSection icon="📊" title="TOP MOVERS – SCORE CHANGES" defaultOpen={false}>
            {/* PLUS MOVERS */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "0.66rem", color: "#00acc1", fontWeight: "900", letterSpacing: "1px", marginBottom: "8px" }}>
                🚀 GRÖSSTE GEWINNER ↑
              </div>
              {stats.topPlusMovers.length === 0 ? (
                <div style={{ fontSize: "0.72rem", color: "#444", padding: "6px 0" }}>Noch keine Daten</div>
              ) : stats.topPlusMovers.map(t => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", padding: "8px 10px", background: "rgba(0,172,193,0.04)", borderRadius: "8px", border: "1px solid rgba(0,172,193,0.1)", marginBottom: "4px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.84rem", color: "#ddd", fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                    <div style={{ fontSize: "0.66rem", color: "#666" }}>{t.artist}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.78rem", color: "#00acc1", fontWeight: "900" }}>+{t.qScoreDelta}</div>
                    <div style={{ fontSize: "0.6rem", color: "#555" }}>Q{t.prevQScore}→Q{t.qScore}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* MINUS MOVERS */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "0.66rem", color: "#ff1744", fontWeight: "900", letterSpacing: "1px", marginBottom: "8px" }}>
                📉 GRÖSSTE VERLIERER ↓
              </div>
              {stats.topMinusMovers.length === 0 ? (
                <div style={{ fontSize: "0.72rem", color: "#444", padding: "6px 0" }}>Keine negativen Movements</div>
              ) : stats.topMinusMovers.map(t => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", padding: "8px 10px", background: "rgba(255,23,68,0.04)", borderRadius: "8px", border: "1px solid rgba(255,23,68,0.1)", marginBottom: "4px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.84rem", color: "#999", fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
                    <div style={{ fontSize: "0.66rem", color: "#555" }}>
                      {t.artist}
                      {t.onAir ? <span style={{ color: "#00acc1", marginLeft: "6px" }}>📡 On Air</span> : <span style={{ color: "#333", marginLeft: "6px" }}>○ Off Air</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.78rem", color: "#ff1744", fontWeight: "900" }}>{t.qScoreDelta}</div>
                    <div style={{ fontSize: "0.6rem", color: "#555" }}>Q{t.prevQScore}→Q{t.qScore}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* STAGNANTE TRACKS */}
            {stats.stagnantTracks.length > 0 && (
              <div>
                <div style={{ fontSize: "0.66rem", color: "#555", fontWeight: "900", letterSpacing: "1px", marginBottom: "8px" }}>
                  💤 KEINE BEWEGUNG ({stats.stagnantTracks.length})
                </div>
                {stats.stagnantTracks.map(t => (
                  <div key={t.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #111" }}>
                    <span style={{ fontSize: "0.78rem", color: "#555" }}>{t.title}</span>
                    <span style={{ fontSize: "0.66rem", color: "#444" }}>{t.plays} Plays gesamt · Q{t.qScore}</span>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>

          {/* ON/OFF AIR */}
          <CollapsibleSection icon="📡" title={`ON / OFF AIR – LETZTE ${zeitraumLabel}`} count={stats.onAirMovements.length} defaultOpen={false}>
            {stats.onAirMovements.length === 0 ? (
              <div style={{ fontSize: "0.72rem", color: "#555", textAlign: "center", padding: "10px" }}>
                Keine Bewegungen in diesem Zeitraum
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {stats.newOnAir.length > 0 && (
                  <div style={{ marginBottom: "8px" }}>
                    <div style={{ fontSize: "0.66rem", color: "#00acc1", fontWeight: "900", letterSpacing: "1px", marginBottom: "6px" }}>
                      📡 NEU ON AIR ({stats.newOnAir.length})
                    </div>
                    {stats.newOnAir.map((m, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(0,172,193,0.05)", borderRadius: "6px", marginBottom: "4px", border: "1px solid rgba(0,172,193,0.1)" }}>
                        <div>
                          <div style={{ fontSize: "0.78rem", color: "#ddd" }}>{m.trackTitle}</div>
                          <div style={{ fontSize: "0.66rem", color: "#666" }}>{m.trackArtist}</div>
                        </div>
                        <div style={{ fontSize: "0.6rem", color: "#555" }}>
                          {new Date(m.changedAt).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {stats.newOffAir.length > 0 && (
                  <div>
                    <div style={{ fontSize: "0.66rem", color: "#ff1744", fontWeight: "900", letterSpacing: "1px", marginBottom: "6px" }}>
                      ○ OFF AIR GEGANGEN ({stats.newOffAir.length})
                    </div>
                    {stats.newOffAir.map((m, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(255,23,68,0.05)", borderRadius: "6px", marginBottom: "4px", border: "1px solid rgba(255,23,68,0.1)" }}>
                        <div>
                          <div style={{ fontSize: "0.78rem", color: "#999" }}>{m.trackTitle}</div>
                          <div style={{ fontSize: "0.66rem", color: "#555" }}>{m.trackArtist}</div>
                        </div>
                        <div style={{ fontSize: "0.6rem", color: "#555" }}>
                          {new Date(m.changedAt).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* NEUE MEMBERS */}
          {stats.newUsers.length > 0 && (
            <CollapsibleSection icon="👥" title={`NEUE MEMBERS – LETZTE ${zeitraumLabel}`} count={stats.newUsers.length} defaultOpen={false}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {stats.newUsers.map(u => (
                  <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "rgba(255,255,255,0.02)", borderRadius: "8px", border: "1px solid #1a1a1a" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: u.avatar ? `url(${u.avatar}) center/cover` : "#111", border: "1px solid #333", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.84rem", color: "#ddd" }}>{u.name || u.email}</span>
                    </div>
                    <span style={{ fontSize: "0.66rem", color: "#555" }}>
                      {u.role === "artist" ? "🎙️" : u.role === "pending" ? "⏳" : "🎧"} {u.role?.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* INAKTIVE ARTISTS */}
          {stats.inactiveArtists.length > 0 && (
            <CollapsibleSection icon="⚠️" title="INAKTIVE ARTISTS >30 TAGE" count={stats.inactiveArtists.length} defaultOpen={false}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {stats.inactiveArtists.map(u => (
                  <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "rgba(255,152,0,0.04)", borderRadius: "8px", border: "1px solid rgba(255,152,0,0.15)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: u.avatar ? `url(${u.avatar}) center/cover` : "#111", border: "1px solid #333", flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: "0.84rem", color: "#ddd" }}>{u.name || u.email}</div>
                        <div style={{ fontSize: "0.66rem", color: "#666" }}>
                          Letzter Login: {formatDate(u.lastLogin?.seconds)}
                          {!u.email && <span style={{ color: "#ff1744", marginLeft: "6px" }}>⚠️ keine Email</span>}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSendReminder(u)}
                      disabled={sendingReminder === u.id || !u.email}
                      style={{
                        background: "none",
                        border: `1px solid ${u.email ? "rgba(255,152,0,0.3)" : "rgba(255,255,255,0.1)"}`,
                        color: u.email ? "#ff9800" : "#333",
                        padding: "5px 10px", borderRadius: "6px",
                        fontSize: "0.66rem", fontWeight: "900",
                        cursor: u.email ? "pointer" : "not-allowed",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {sendingReminder === u.id ? "SENDET..." : "✉️ ERINNERN"}
                    </button>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}


          {/* WEEKLY ARTIST REPORT PANEL */}
          <CollapsibleSection icon="📧" title="ARTIST WEEKLY REPORT" defaultOpen={false}>
            <div style={{ padding: "15px", background: "rgba(0,172,193,0.04)", borderRadius: "12px", border: "1px solid rgba(0,172,193,0.2)" }}>
              <label style={{ fontSize: "0.66rem", color: "#00acc1", fontWeight: "900", display: "block", marginBottom: "8px" }}>
                ✨ NEUE FEATURES (WIRD AN ALLE GESENDET)
              </label>
              <textarea
                value={newFeaturesText}
                onChange={(e) => setNewFeaturesText(e.target.value)}
                placeholder="Beschreibe kurz neue Funktionen..."
                style={{
                  width: "100%", height: "80px", background: "#050505", border: "1px solid #1a1a1a",
                  borderRadius: "8px", color: "#ccc", padding: "10px", fontSize: "0.78rem",
                  fontFamily: "sans-serif", resize: "none", marginBottom: "12px"
                }}
              />
              <button
                onClick={() => handleSendWeeklyReport()}
                disabled={sendingWeeklyReport}
                style={{
                  width: "100%", padding: "12px", 
                  background: weeklyReportSent ? "#2e7d32" : "#00acc1", 
                  color: "#000", border: "none", borderRadius: "10px", 
                  fontSize: "0.72rem", fontWeight: "900", cursor: "pointer"
                }}
              >
                {sendingWeeklyReport ? "GENERIERRE & SENDE REPORTS..." : weeklyReportSent ? "BERICHTE ERFOLGREICH VERSENDET ✅" : "🚀 SEND WEEKLY REPORT TO ALL ARTISTS"}
              </button>
              {weeklyReportSent && (
                <p style={{ fontSize: "0.6rem", color: "#555", marginTop: "8px", textAlign: "center" }}>
                  Die Emails wurden individuell generiert und via Resend versendet.
                </p>
              )}
            </div>
          </CollapsibleSection>


          {/* EMAIL EXPORT */}
          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: "16px", display: "flex", gap: "8px" }}>
            <button
              onClick={handleSendEmail}
              disabled={sendingEmail}
              style={{ flex: 1, padding: "12px", background: "rgba(0,172,193,0.08)", border: "1px solid rgba(0,172,193,0.2)", borderRadius: "10px", color: "#00acc1", fontSize: "0.72rem", fontWeight: "900", letterSpacing: "1px", cursor: "pointer" }}
            >
              {sendingEmail ? "SENDET..." : "📧 RESUMÉ EMAIL SENDEN"}
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `STREETWISE RADIO – RESUMÉ KW ${stats.kw}\n\n` +
                  (aiSummary ? aiSummary + "\n\n" : "") +
                  `Plays: ${stats.periodPlays} | Completion: ${stats.completionRate}% | Likes: ${stats.periodLikes}`
                );
                showToast?.("Resumé kopiert! ✅");
              }}
              style={{ flex: 1, padding: "12px", background: "rgba(255,255,255,0.02)", border: "1px solid #1a1a1a", borderRadius: "10px", color: "#666", fontSize: "0.72rem", fontWeight: "900", letterSpacing: "1px", cursor: "pointer" }}
            >
              📋 KOPIEREN
            </button>
          </div>

        </div>
      )}
    </div>
  );
}